import { useAuthState } from '@auth';
import { CommunityAuthRequired, CommunityErrorState, LoadingMessage as CommunityLoadingState } from '@communities/community/components';
import { CommunityDialog } from '@communities/community/components/CommunityDialog';
import { useCommunityDetail } from '@communities/community/hooks';
import { EntityType } from '@communities/content-management/domain';
import { TabNavigation } from '@communities/content-management/pages';
import { HeroBackground } from '@core/components';
import { withAppInsightsTracking } from '@core/logging/withAppInsightsTracking';
import { useState } from 'react';

import * as styles from './CommunityDetailPage.css.ts';
import { CommunityHeaderSection } from './sections/CommunityHeaderSection.tsx';

/**
 * Community Detail Page Component
 *
 * Displays detailed information about a community
 */

function CommunityDetailPageComponent() {
  const { isAuthenticated } = useAuthState();

  const { community, isCommunityAdmin, loading, errors, refetch } = useCommunityDetail();

  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);

  const handleEditCommunity = () => {
    setIsEditDialogOpen(true);
  };

  const handleEditDialogClose = () => {
    setIsEditDialogOpen(false);
  };

  // Authentication check
  if (!isAuthenticated) {
    return <CommunityAuthRequired />;
  }

  // Error states - only show if not loading and have errors
  if (errors && errors.length > 0 && !loading) {
    return <CommunityErrorState errors={errors} onRetry={() => refetch()} />;
  }

  return (
    <div>
      {/* Hero Background Component - always renders */}
      <HeroBackground imageUrl={community?.superHeroArtUrl} />

      {loading ? (
        <CommunityLoadingState />
      ) : !community ? null : (
        <>
          {/* Header Section with Background Image */}
          <CommunityHeaderSection community={community} />

          {/* Content Section with Solid Background */}
          <div className={styles.contentSection}>
            {/* Edit Community Dialog */}
            <CommunityDialog initialCommunity={community} isOpen={isEditDialogOpen} onClose={handleEditDialogClose} />

            {/* Tab Content with Navigation */}
            <div className={styles.tabContent}>
              <TabNavigation
                communityGroupId={community.communityGroupId}
                entityId={community.id}
                entityType={EntityType.COMMUNITY}
                isEntityAdmin={isCommunityAdmin}
                onEditCommunity={isCommunityAdmin ? handleEditCommunity : undefined}
              />
            </div>
          </div>
        </>
      )}
    </div>
  );
}

export const CommunityDetailPage = withAppInsightsTracking(CommunityDetailPageComponent, 'CommunityDetailPage');
